import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Collapse,
} from '@mui/material';
import {
  Mic,
  Delete,
  PlayArrow,
  Pause,
  Stop,
  DragIndicator,
  Psychology,
  ExpandMore,
  ExpandLess,
  ChevronLeft,
  ChevronRight,
  QueueMusic,
  GraphicEq,
  Campaign,
  Public,
  Schedule,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import io from 'socket.io-client';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import VoicetrackMarkerRecorder from '../components/VoicetrackMarkerRecorder';
import AiInsightsDialog from '../components/AiInsightsDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const formatLocalDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function EqualizerBars({ color = '#22d3ee', height = 12 }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '2px', height, mr: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 2.5,
            height: '100%',
            borderRadius: 1,
            bgcolor: color,
            transformOrigin: 'bottom',
            animation: `eq-bounce ${0.8 + i * 0.12}s ease-in-out ${i * 0.14}s infinite`,
          }}
        />
      ))}
    </Box>
  );
}

// Compact console-style readout: small uppercase label + mono value
function StatPill({ label, value, accent }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        bgcolor: 'rgba(148,163,184,0.07)',
        border: '1px solid rgba(148,163,184,0.14)',
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: '0.6rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--rs-muted)',
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        className="tabular-nums"
        sx={{
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          fontSize: '0.78rem',
          fontWeight: 600,
          color: accent || '#7dd3fc',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function VoiceTrack() {
  const { user, hasSystemFeature } = useAuth();
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [socket, setSocket] = useState(null);
  const selectedStationRef = useRef(null);
  const fetchPlaylistRef = useRef(null);

  // Playlist selection
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(formatLocalDate);
  const [selectedHour, setSelectedHour] = useState(new Date().getHours());
  const [playlistItems, setPlaylistItems] = useState([]);
  const [selectedPlaylistIndex, setSelectedPlaylistIndex] = useState(null);
  const [loadingPlaylist, setLoadingPlaylist] = useState(false);
  const [savingPlaylist, setSavingPlaylist] = useState(false);

  // Voicetrack marker state
  const [selectedMarkerItem, setSelectedMarkerItem] = useState(null);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState(-1);
  const [showVoicetrackRecorder, setShowVoicetrackRecorder] = useState(false);
  
  // Audio playback state
  const [playingIndex, setPlayingIndex] = useState(null);
  const [playingVoicetrackId, setPlayingVoicetrackId] = useState(null); // When playing a specific saved VT (split)
  const audioRef = useRef(null);
  
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  
  // Selection state for inserting VT markers
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Collapsible commercial break groups
  const [expandedBreaks, setExpandedBreaks] = useState(new Set());
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [aiInsightsOpen, setAiInsightsOpen] = useState(false);
  const [aiInsightsItem, setAiInsightsItem] = useState(null);

  // Region support for split voicetracks
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null); // null = master/shared, or region_id
  const [splitVTGroups, setSplitVTGroups] = useState([]);
  const [savedVoicetracksForMarker, setSavedVoicetracksForMarker] = useState([]); // Voicetracks for current marker (for recorder display)
  const [voicetracksByPlayoutLog, setVoicetracksByPlayoutLog] = useState({}); // { playoutLogId: [vt, ...] } for playlist display
  const [voicetracksByIndex, setVoicetracksByIndex] = useState({}); // { playlistIndex: [vt, ...] } for markers without playout_log_id

  // Playlist editing
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [musicBedCategories, setMusicBedCategories] = useState([]);
  const [musicBedAssets, setMusicBedAssets] = useState([]);

  useEffect(() => {
    // Connect to WebSocket
    const newSocket = io(API_URL, { withCredentials: true });
    setSocket(newSocket);

    // Real-time playout status: refresh playlist when the current hour changes
    newSocket.on('playout:status', (data) => {
      if (data?.regionId != null) return;
      if (data?.stationId && String(data.stationId) === String(selectedStationRef.current)) {
        if (fetchPlaylistRef.current) fetchPlaylistRef.current();
      }
    });

    // When a voicetrack recording has been processed and saved, refresh the view
    newSocket.on('voicetrack:saved', (data) => {
      if (data?.stationId && String(data.stationId) === String(selectedStationRef.current)) {
        if (fetchPlaylistRef.current) fetchPlaylistRef.current();
      }
    });

    // Enumerate audio input devices - request permission first
    const enumerateDevices = async () => {
      let permissionStream = null;
      try {
        // Request permission to access microphone (this ensures device labels are available)
        permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Now enumerate devices with labels
        const deviceList = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = deviceList.filter(
          (device) => device.kind === 'audioinput'
        );
        setDevices(audioInputs);
        if (audioInputs.length > 0) {
          setSelectedDevice(audioInputs[0].deviceId);
        }
      } catch (err) {
        console.error('Error enumerating devices:', err);
        // Try without permission (labels may be empty)
        await navigator.mediaDevices.enumerateDevices().then((deviceList) => {
          const audioInputs = deviceList.filter(
            (device) => device.kind === 'audioinput'
          );
          setDevices(audioInputs);
          if (audioInputs.length > 0) {
            setSelectedDevice(audioInputs[0].deviceId);
          }
        });
      } finally {
        permissionStream?.getTracks().forEach((track) => track.stop());
      }
    };
    
    enumerateDevices();

    // Fetch stations
    fetchStations();

    return () => {
      newSocket.close();
    };
  }, []);

  // Auto-select station if only one exists
  useEffect(() => {
    if (stations.length === 1 && !selectedStation) {
      setSelectedStation(stations[0].id);
    } else if (!selectedStation && user?.stationId) {
      setSelectedStation(user.stationId);
    }
  }, [stations, user, selectedStation]);

  // Keep refs current so socket handlers always use the latest values
  useEffect(() => { selectedStationRef.current = selectedStation; }, [selectedStation]);
  useEffect(() => { fetchPlaylistRef.current = fetchPlaylist; });

  // Fetch regions when station changes
  useEffect(() => {
    if (selectedStation) {
      fetchRegions();
      fetchCategories();
    }
  }, [selectedStation]);

  // Fetch voicetracks for the current marker when recorder opens (for split display)
  // Use playout_log_id when available, else playlist_date/hour/index (for markers without DB id)
  useEffect(() => {
    if (showVoicetrackRecorder && selectedStation) {
      const hasMarkerId = selectedMarkerItem?.id;
      const hasPlaylistContext = selectedDate && selectedHour !== null && selectedMarkerIndex >= 0;
      if (hasMarkerId || hasPlaylistContext) {
        fetchVoicetracksForMarker(hasMarkerId ? selectedMarkerItem.id : null);
      } else {
        setSavedVoicetracksForMarker([]);
      }
    } else {
      setSavedVoicetracksForMarker([]);
    }
  }, [showVoicetrackRecorder, selectedMarkerItem?.id, selectedStation, selectedDate, selectedHour, selectedMarkerIndex]);

  const fetchRegions = async () => {
    if (!selectedStation) return;
    try {
      const res = await axios.get(`${API_URL}/api/regions?station_id=${selectedStation}`, { withCredentials: true });
      setRegions(res.data || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchVoicetracksForMarker = async (playoutLogId) => {
    if (!selectedStation) return;
    try {
      const params = { station_id: selectedStation };

      // Prefer DB marker ID when available
      if (playoutLogId) {
        params.playout_log_id = playoutLogId;
      } else {
        // Fallback for generated markers without DB id: match by playlist slot context
        params.playlist_date = selectedDate;
        params.playlist_hour = selectedHour;
        params.playlist_index = selectedMarkerIndex;
      }

      const res = await axios.get(`${API_URL}/api/voicetrack-enhanced`, {
        params,
        withCredentials: true
      });
      setSavedVoicetracksForMarker(res.data || []);
    } catch (error) {
      console.error('Error fetching voicetracks for marker:', error);
      setSavedVoicetracksForMarker([]);
    }
  };

  const fetchSplitVTGroups = async () => {
    if (!selectedStation || !selectedDate || selectedHour === null) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/regions/split-vt-groups?station_id=${selectedStation}&date=${selectedDate}&hour=${selectedHour}`,
        { withCredentials: true }
      );
      setSplitVTGroups(res.data || []);
    } catch (error) {
      console.error('Error fetching split VT groups:', error);
    }
  };

  // Fetch playlist when station/date/hour/region changes
  useEffect(() => {
    if (selectedStation && selectedDate && selectedHour !== null) {
      fetchPlaylist();
      fetchAssets();
      fetchCategories();
      fetchSplitVTGroups();
    }
  }, [selectedStation, selectedDate, selectedHour, selectedRegion]);

  // Fetch categories
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchAssets = async () => {
    if (!selectedStation) return;
    try {
      const res = await axios.get(`${API_URL}/api/assets`, {
        params: { station_id: selectedStation, playout_only: true },
        withCredentials: true
      });
      setAssets(res.data || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`, {
        params: selectedStation ? { station_id: selectedStation } : {},
        withCredentials: true
      });
      setCategories(res.data || []);
      fetchMusicBedCategories(res.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchMusicBedCategories = async (allCategories) => {
    try {
      const musicBedParent = allCategories.find(c => c.category_type === 'music_bed' && !c.parent_category_id);
      if (musicBedParent) {
        const subRes = await axios.get(`${API_URL}/api/categories/${musicBedParent.id}/subcategories`, {
          params: selectedStation ? { station_id: selectedStation } : {},
          withCredentials: true
        });
        const subs = subRes.data || [];
        setMusicBedCategories([musicBedParent, ...subs]);

        // Fetch assets in all music bed categories
        const allCatIds = [musicBedParent.id, ...subs.map(s => s.id)];
        const assetRes = await axios.get(`${API_URL}/api/assets`, {
          params: { station_id: selectedStation, category_ids: allCatIds.join(','), playout_only: true },
          withCredentials: true
        });
        setMusicBedAssets(assetRes.data || []);
      } else {
        setMusicBedCategories([]);
        setMusicBedAssets([]);
      }
    } catch (error) {
      console.error('Error fetching music bed categories:', error);
    }
  };

  const fetchStations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stations`, {
        withCredentials: true
      });
      const list = res.data || [];
      setStations(list);
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const fetchVoicetracksForPlaylist = async (items) => {
    if (!selectedStation || !items?.length) return;
    const markerIds = [...new Set(items.filter(i => (i.type === 'voicetrack' || i.type === 'voice') && i.id).map(i => i.id))];
    const vtMarkersWithoutId = items
      .map((i, idx) => ({ item: i, index: idx }))
      .filter(({ item }) => (item.type === 'voicetrack' || item.type === 'voice') && !item.id);

    const byPlayoutLog = {};
    const byIndex = {};

    try {
      // Fetch by playout_log_id for markers that have one
      if (markerIds.length > 0) {
        const res = await axios.get(`${API_URL}/api/voicetrack-enhanced`, {
          params: { station_id: selectedStation, playout_log_ids: markerIds.join(',') },
          withCredentials: true
        });
        const list = res.data || [];
        list.forEach(vt => {
          const pid = vt.playout_log_id;
          if (!byPlayoutLog[pid]) byPlayoutLog[pid] = [];
          byPlayoutLog[pid].push(vt);
        });
      }
      setVoicetracksByPlayoutLog(byPlayoutLog);

      // Fetch by playlist_date + playlist_hour for markers without id (e.g. from M3U)
      if (vtMarkersWithoutId.length > 0 && selectedDate && selectedHour !== null) {
        const res = await axios.get(`${API_URL}/api/voicetrack-enhanced`, {
          params: {
            station_id: selectedStation,
            playlist_date: selectedDate,
            playlist_hour: selectedHour
          },
          withCredentials: true
        });
        const list = res.data || [];
        list.forEach(vt => {
          const idx = vt.playlist_index;
          if (idx !== undefined && idx !== null) {
            if (!byIndex[idx]) byIndex[idx] = [];
            byIndex[idx].push(vt);
          }
        });
        setVoicetracksByIndex(byIndex);
      } else {
        setVoicetracksByIndex({});
      }
    } catch (error) {
      console.error('Error fetching voicetracks for playlist:', error);
      setVoicetracksByPlayoutLog({});
      setVoicetracksByIndex({});
    }
  };

  const fetchPlaylist = async () => {
    if (!selectedStation || !selectedDate || selectedHour === null) return;
    
    setLoadingPlaylist(true);
    try {
      let res;
      const baseUrl = `${API_URL}/api/playlist-generator/${selectedStation}/${selectedDate}/${selectedHour}`;
      
      if (selectedRegion) {
        // Fetch regional playlist when a region is selected
        try {
          res = await axios.get(`${baseUrl}/region/${selectedRegion}`, { withCredentials: true });
        } catch (regionalError) {
          if (regionalError.response?.status === 404) {
            // Regional playlist doesn't exist yet - try to generate it
            try {
              await axios.post(`${API_URL}/api/playlist-generator/generate/regional`, {
                station_id: parseInt(selectedStation),
                date: selectedDate,
                start_hour: selectedHour,
                end_hour: selectedHour,
                region_id: parseInt(selectedRegion)
              }, { withCredentials: true });
              res = await axios.get(`${baseUrl}/region/${selectedRegion}`, { withCredentials: true });
            } catch (genErr) {
              // Fall back to master playlist if generation fails
              console.warn('Could not generate regional playlist, falling back to master:', genErr);
              res = await axios.get(baseUrl, { withCredentials: true });
            }
          } else {
            throw regionalError;
          }
        }
      } else {
        res = await axios.get(baseUrl, { withCredentials: true });
      }
      
      let items = res.data.items || [];
      
      // Sort items by startTime to match Playlists tab order
      items = items.sort((a, b) => {
        if (!a.startTime && !b.startTime) return 0;
        if (!a.startTime) return 1;
        if (!b.startTime) return -1;
        return new Date(a.startTime) - new Date(b.startTime);
      });
      
      setPlaylistItems(items);

      // Fetch voicetracks for all markers (including split per region) for play buttons
      fetchVoicetracksForPlaylist(items);
      
      // Find first voicetrack item or allow user to select
      const vtIndex = items.findIndex(item => item.type === 'voicetrack');
      if (vtIndex >= 0) {
        setSelectedPlaylistIndex(vtIndex);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setPlaylistItems([]);
      } else if (error.message === 'Network Error') {
        console.error('Playlist fetch failed - backend may be unreachable:', error);
        alert('Failed to load playlist: Network Error. Is the backend server running?');
      } else {
        console.error('Error fetching playlist:', error);
        alert('Failed to load playlist: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoadingPlaylist(false);
    }
  };


  // Handle item selection for VT marker insertion
  const handleItemSelect = (index) => {
    // Don't allow selecting voicetrack markers
    const item = playlistItems[index];
    if (item.type === 'voicetrack' || item.type === 'voice') {
      return;
    }

    setSelectedItems(prev => {
      // If item is already selected, deselect it
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      }
      // If we have 2 items selected, replace the first one
      if (prev.length >= 2) {
        return [prev[1], index];
      }
      // Otherwise add to selection
      return [...prev, index];
    });
  };

  // Add VT marker
  const addVTMarker = async () => {
    try {
      let insertIndex;
      let startTime;

      if (selectedItems.length === 2) {
        // Insert between the two selected items (copy first — .sort() mutates, and selectedItems is React state)
        const [firstIndex, secondIndex] = [...selectedItems].sort((a, b) => a - b);
        insertIndex = firstIndex + 1;
        
        // Calculate start time based on the first selected item
        const firstItem = playlistItems[firstIndex];
        if (firstItem.startTime) {
          const firstItemTime = new Date(firstItem.startTime);
          const duration = firstItem.duration || 0;
          startTime = new Date(firstItemTime.getTime() + duration * 1000);
        } else {
          // Fallback: calculate from cumulative time
          let cumulativeTime = 0;
          for (let i = 0; i < firstIndex; i++) {
            cumulativeTime += playlistItems[i].duration || 0;
          }
          cumulativeTime += firstItem.duration || 0;
          const hourStart = new Date(`${selectedDate}T${selectedHour.toString().padStart(2, '0')}:00:00`);
          startTime = new Date(hourStart.getTime() + cumulativeTime * 1000);
        }
      } else {
        // Insert at the end
        insertIndex = playlistItems.length;
        if (playlistItems.length > 0) {
          const lastItem = playlistItems[playlistItems.length - 1];
          if (lastItem.startTime) {
            const lastItemTime = new Date(lastItem.startTime);
            const duration = lastItem.duration || 0;
            startTime = new Date(lastItemTime.getTime() + duration * 1000);
          } else {
            const hourStart = new Date(`${selectedDate}T${selectedHour.toString().padStart(2, '0')}:00:00`);
            const totalDuration = playlistItems.reduce((sum, item) => sum + (item.duration || 0), 0);
            startTime = new Date(hourStart.getTime() + totalDuration * 1000);
          }
        } else {
          const hourStart = new Date(`${selectedDate}T${selectedHour.toString().padStart(2, '0')}:00:00`);
          startTime = hourStart;
        }
      }

      const newMarker = {
        type: 'voicetrack',
        title: 'VT Marker',
        duration: 0,
        startTime: startTime.toISOString()
      };
      
      const updatedItems = [...playlistItems];
      updatedItems.splice(insertIndex, 0, newMarker);
      
      const recalculated = recalculateTimes(updatedItems);
      await savePlaylist(recalculated);
      
      // Refresh playlist to get IDs for newly created items
      await fetchPlaylist();
      
      // Clear selection after adding marker
      setSelectedItems([]);
    } catch (error) {
      console.error('Error adding VT marker:', error);
      alert('Failed to add VT marker: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handle marker click to open recorder
  const handleMarkerClick = (item, index) => {
    if (item.type === 'voicetrack' || item.type === 'voice') {
      setSelectedMarkerItem(item);
      setSelectedMarkerIndex(index);
      setShowVoicetrackRecorder(true);
    }
  };

  // Handle voicetrack save
  const handleVoicetrackSave = async (audioBlob, settings) => {
    try {
      if (!selectedMarkerItem) {
        alert('No marker selected');
        return;
      }

      if (!selectedStation) {
        alert('No station selected');
        return;
      }

      const formData = new FormData();
      formData.append('audio', audioBlob, 'voicetrack.webm');
      formData.append('station_id', selectedStation.toString());
      
      // Send playout_log_id if the marker item has an id (from database)
      if (selectedMarkerItem.id) {
        formData.append('playout_log_id', selectedMarkerItem.id.toString());
      }
      
      // Also send playlist_index for backwards compatibility
      formData.append('playlist_index', selectedMarkerIndex.toString());
      
      // Send date and hour so backend can check if it's current hour
      formData.append('playlist_date', selectedDate);
      formData.append('playlist_hour', selectedHour.toString());
      
      // Duration should be in seconds (round to integer)
      const duration = Math.round(settings.duration || 0);
      formData.append('duration', duration.toString());
      formData.append('content', 'Voicetrack');
      const useAsSweeper = !!(settings.use_as_sweeper);
      formData.append('is_sweeper', useAsSweeper ? 'true' : 'false');
      formData.append('play_as', useAsSweeper ? 'sweeper' : 'normal');

      // Music bed settings
      if (settings.music_bed_enabled) {
        formData.append('music_bed_enabled', 'true');
        formData.append('music_bed_selection_mode', settings.music_bed_selection_mode || 'random_any');
        if (settings.music_bed_category_id) {
          formData.append('music_bed_category_id', settings.music_bed_category_id.toString());
        }
        if (settings.music_bed_asset_id) {
          formData.append('music_bed_asset_id', settings.music_bed_asset_id.toString());
        }
        formData.append('music_bed_volume_db', (settings.music_bed_volume_db ?? -12).toString());
        formData.append('sweep_over_intro', settings.sweep_over_intro ? 'true' : 'false');
      }

      // Region support for split voicetracks
      if (settings.region_id) {
        formData.append('region_id', settings.region_id.toString());
        formData.append('is_split_vt', 'true');
      }

      // Segue settings (manual positioning + ducking volumes from the segue editor)
      if (settings.vt_position !== undefined && settings.vt_position !== null) {
        formData.append('vt_position', settings.vt_position.toString());
      }
      if (settings.vt_gap !== undefined && settings.vt_gap !== null) {
        formData.append('vt_gap', settings.vt_gap.toString());
      }
      if (settings.volumes) {
        formData.append('volumes', JSON.stringify(settings.volumes));
      }
      if (settings.segue) {
        formData.append('segue', JSON.stringify(settings.segue));
      }

      const replaceVtId = settings.replace_vt_id;

      console.log('Saving voicetrack with data:', {
        station_id: selectedStation,
        playout_log_id: selectedMarkerItem.id,
        playlist_index: selectedMarkerIndex,
        duration: duration,
        replace_vt_id: replaceVtId || undefined
      });

      let res;
      if (replaceVtId && settings.region_id) {
        formData.append('station_id', selectedStation.toString());
        res = await axios.patch(`${API_URL}/api/voicetrack-enhanced/${replaceVtId}/audio`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        });
      } else {
        res = await axios.post(`${API_URL}/api/voicetrack-enhanced`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        });
      }

      console.log('Voicetrack saved successfully:', res.data);

      const isSplitRegionSave = !!(settings.region_id);

      // For SPLIT voicetracks (region-specific): do NOT update the master playlist.
      // The master playlist should keep the generic VT marker.
      // Regional playlists are generated separately and will use the correct regional VT.
      if (isSplitRegionSave) {
        console.log(`[VoiceTrack] Split VT saved for region ${settings.region_id} - NOT updating master playlist`);
        // Regional regeneration is handled server-side in voicetrack-enhanced route.
        alert('Split voicetrack saved successfully!');
        
        // Refresh saved voicetracks for this marker so the recorder can show them with play buttons
        if (res.data?.id) {
          fetchVoicetracksForMarker(selectedMarkerItem?.id || null);
          fetchVoicetracksForPlaylist(playlistItems);
        }
        
        // Re-fetch the playlist view after a short delay so the regenerated regional
        // playlist is picked up if the user is viewing a regional playlist.
        // The backend regenerates M3U files synchronously before responding, so this
        // just needs a small delay for the response to finish.
        setTimeout(() => {
          fetchPlaylist();
        }, 1500);
      } else {
        // SHARED voicetrack (no region) — update the master playlist as before
        if (res.data && selectedMarkerItem) {
          const updatedItems = playlistItems.map((item, idx) => {
            if (idx === selectedMarkerIndex) {
              const filepath = res.data.asset?.filepath || res.data.filepath || res.data.asset?.filename || res.data.filename;
              const recordingDuration = res.data.asset?.duration || res.data.duration || duration;
              
              const artistName = user?.username || 
                                (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : null) ||
                                user?.firstName ||
                                'VT';
              
              const useAsSweeper = !!(settings.use_as_sweeper);
              return {
                ...item,
                type: 'voicetrack',
                asset_id: res.data.asset?.id || null,
                duration: recordingDuration,
                filepath: filepath,
                voicetrack_id: res.data.id,
                title: res.data.content || 'Voicetrack',
                artist: artistName,
                is_sweeper: useAsSweeper,
                play_as: useAsSweeper ? 'sweeper' : 'normal',
                // Carry the segue settings so the backend can translate them into
                // playout markers (talk over outro / duck next intro) on save.
                segue: settings.segue || null,
              };
            }
            return item;
          });
          
          const recalculated = recalculateTimes(updatedItems);
          setPlaylistItems(recalculated);
          await savePlaylist(recalculated);
        }

        alert('Voicetrack saved successfully!');
        setShowVoicetrackRecorder(false);
        setSelectedMarkerItem(null);
        setSelectedMarkerIndex(-1);
      }
    } catch (error) {
      console.error('Error saving voicetrack:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      console.error('Full error response:', error.response?.data);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      alert('Failed to save voicetrack: ' + errorMessage);
    }
  };


  const handleRemoveItem = async (index) => {
    if (!window.confirm('Remove this item from the playlist?')) {
      return;
    }

    try {
      const updatedItems = playlistItems.filter((_, i) => i !== index);
      await savePlaylist(updatedItems);
      
      // Adjust selected index if needed
      if (selectedPlaylistIndex === index) {
        setSelectedPlaylistIndex(null);
      } else if (selectedPlaylistIndex > index) {
        setSelectedPlaylistIndex(selectedPlaylistIndex - 1);
      }
    } catch (error) {
      console.error('Error removing item:', error);
      alert('Failed to remove item: ' + (error.response?.data?.message || error.message));
    }
  };

  const savePlaylist = async (items) => {
    if (!selectedStation || !selectedDate || selectedHour === null) return;

    setSavingPlaylist(true);
    try {
      // Always save to the MASTER playlist endpoint.
      // The backend regenerates regional playlists synchronously before responding.
      await axios.put(
        `${API_URL}/api/playlist-generator/${selectedStation}/${selectedDate}/${selectedHour}`,
        { items },
        { withCredentials: true }
      );
      
      // After the master is saved (and regionals regenerated on the backend),
      // always refetch the playlist to get the correct view (master or regional).
      await fetchPlaylist();
    } catch (error) {
      console.error('Error saving playlist:', error);
      throw error;
    } finally {
      setSavingPlaylist(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate cumulative start times for playlist items (as actual time of day)
  const calculateItemTimings = () => {
    if (!selectedDate || selectedHour === null || playlistItems.length === 0) {
      return playlistItems.map(item => ({
        ...item,
        startTime: item.startTime || null,
        formattedStartTime: item.startTime ? formatTime(item.startTime) : 'N/A'
      }));
    }

    // Calculate hour start as a Date object
    const hourStart = new Date(`${selectedDate}T${selectedHour.toString().padStart(2, '0')}:00:00`);
    let currentTime = new Date(hourStart);

    return playlistItems.map((item) => {
      const startTime = new Date(currentTime);
      const duration = item.duration || 0;
      
      currentTime = new Date(startTime.getTime() + duration * 1000);

      return {
        ...item,
        startTime: startTime.toISOString(),
        formattedStartTime: formatTime(startTime.toISOString())
      };
    });
  };

  // Group consecutive commercial_break items into collapsible blocks
  // and compute sequential display numbers + cumulative display times
  const groupCommercialBreaks = (items) => {
    const grouped = [];
    let i = 0;
    let displayNum = 1;
    // Cumulative time tracker: start from the first item's time or hour start
    let cumulativeTime = null;
    if (items.length > 0 && items[0].startTime) {
      cumulativeTime = new Date(items[0].startTime);
    } else if (selectedDate && selectedHour !== null) {
      cumulativeTime = new Date(`${selectedDate}T${selectedHour.toString().padStart(2, '0')}:00:00`);
    }

    while (i < items.length) {
      if (items[i].type === 'commercial_break') {
        // Collect consecutive commercial_break items
        const breakGroup = [];
        const groupStartIndex = i;
        while (i < items.length && items[i].type === 'commercial_break') {
          breakGroup.push({ ...items[i], originalIndex: i });
          i++;
        }
        const totalDuration = breakGroup.reduce((sum, s) => sum + (s.duration || 0), 0);
        const groupTime = cumulativeTime ? new Date(cumulativeTime) : null;
        grouped.push({
          type: 'commercial_break_group',
          groupStartIndex,
          displayNum,
          items: breakGroup,
          spotCount: breakGroup.length,
          totalDuration,
          formattedStartTime: groupTime ? formatTime(groupTime.toISOString()) : (breakGroup[0].formattedStartTime || 'N/A'),
          startTime: groupTime ? groupTime.toISOString() : breakGroup[0].startTime,
        });
        displayNum++;
        if (cumulativeTime) {
          cumulativeTime = new Date(cumulativeTime.getTime() + totalDuration * 1000);
        }
      } else {
        const itemTime = cumulativeTime ? new Date(cumulativeTime) : null;
        const duration = items[i].duration || 0;
        grouped.push({
          ...items[i],
          originalIndex: i,
          displayNum,
          formattedStartTime: itemTime ? formatTime(itemTime.toISOString()) : (items[i].formattedStartTime || 'N/A'),
          startTime: itemTime ? itemTime.toISOString() : items[i].startTime,
        });
        displayNum++;
        if (cumulativeTime) {
          cumulativeTime = new Date(cumulativeTime.getTime() + duration * 1000);
        }
        i++;
      }
    }
    return grouped;
  };

  const toggleBreakExpanded = (groupStartIndex) => {
    setExpandedBreaks(prev => {
      const next = new Set(prev);
      if (next.has(groupStartIndex)) {
        next.delete(groupStartIndex);
      } else {
        next.add(groupStartIndex);
      }
      return next;
    });
  };

  // Get category info for an item
  const getCategoryInfo = (item) => {
    let categoryName = 'Unknown';
    let categoryColor = '#666666';

    // Check if category is directly on the item
    if (item.category) {
      if (typeof item.category === 'object' && item.category !== null) {
        if (item.category.name) {
          categoryName = item.category.name;
          categoryColor = item.category.color || '#666666';
        }
      }
    }
    
    // Check if categories array exists
    if (categoryName === 'Unknown' && item.categories && Array.isArray(item.categories) && item.categories.length > 0) {
      const firstCategory = item.categories[0];
      if (firstCategory && typeof firstCategory === 'object' && firstCategory.name) {
        categoryName = firstCategory.name;
        categoryColor = firstCategory.color || '#666666';
      }
    }
    
    // Try to find category from assets list
    else if (item.asset_id) {
      const assetId = typeof item.asset_id === 'string' ? parseInt(item.asset_id, 10) : item.asset_id;
      if (!isNaN(assetId) && assets.length > 0) {
        const asset = assets.find(a => {
          const aId = typeof a.id === 'string' ? parseInt(a.id, 10) : a.id;
          return aId === assetId;
        });
        if (asset && asset.categories && Array.isArray(asset.categories) && asset.categories.length > 0) {
          categoryName = asset.categories[0].name || 'Unknown';
          categoryColor = asset.categories[0].color || '#666666';
        }
      }
    }
    
    // Special types
    if (item.type === 'commercial_break') {
      categoryName = 'Commercial';
      categoryColor = '#f59e0b';
    } else if (item.type === 'voicetrack' || item.type === 'voice') {
      categoryName = 'Voicetrack';
      categoryColor = '#a855f7';
    } else if (item.type === 'ai_voicetrack') {
      categoryName = 'AI Voicetrack';
      categoryColor = '#a855f7';
    } else if (item.type === 'stream_relay') {
      categoryName = 'Stream Relay';
      categoryColor = '#607d8b';
    } else if (item.type === 'hard_fixed_timer') {
      categoryName = 'Hard Timer';
      categoryColor = '#ef4444';
    } else if (item.type === 'mixblock_track') {
      if (categoryName === 'Unknown') {
        categoryName = item.mixblock_name || 'Mixblock';
        categoryColor = '#e040fb';
      }
    }

    return { categoryName, categoryColor };
  };

  // Convert hex color to rgba for background with opacity
  const hexToRgba = (hex, alpha = 0.2) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Recalculate times after reordering
  const recalculateTimes = (items) => {
    if (items.length === 0 || !selectedDate || selectedHour === null) return items;
    
    const hourStart = new Date(`${selectedDate}T${selectedHour.toString().padStart(2, '0')}:00:00`);
    const updated = [];
    let currentTime = new Date(hourStart);
    
    for (const item of items) {
      updated.push({
        ...item,
        startTime: new Date(currentTime).toISOString()
      });
      currentTime = new Date(currentTime.getTime() + (item.duration || 180) * 1000);
    }
    
    return updated;
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...playlistItems];
    const [moved] = updated.splice(draggedIndex, 1);
    updated.splice(index, 0, moved);
    
    const recalculated = recalculateTimes(updated);
    setPlaylistItems(recalculated);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null) {
      // Save the reordered playlist
      savePlaylist(playlistItems);
    }
    setDraggedIndex(null);
  };

  // Audio playback handlers
  const handlePlayItem = (index) => {
    const item = timedPlaylistItems[index];
    
    // Stop current playback if any
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Construct audio URL
    let audioUrl;
    
    // For voicetrack markers, check for voicetrack_id first
    if (item.type === 'voicetrack' && item.voicetrack_id) {
      audioUrl = `${API_URL}/api/voicetrack-enhanced/${item.voicetrack_id}/audio`;
    } else if (item.type === 'voicetrack' && item.filepath) {
      // Fallback: try to use filepath if voicetrack_id not available
      let filename = item.filepath;
      if (filename.includes('/')) {
        filename = filename.split('/').pop();
      }
      // Voicetrack files might be in audio-cache/voicetracks/ or have full path
      audioUrl = `${API_URL}/api/voicetrack-enhanced/audio/${filename}`;
    } else if (item.asset_id) {
      audioUrl = `${API_URL}/api/assets/${item.asset_id}/stream`;
    } else if (item.filepath) {
      let filename = item.filepath;
      if (filename.includes('/')) {
        filename = filename.split('/').pop();
      }
      if (filename.startsWith('uploads/')) {
        filename = filename.replace('uploads/', '');
      }
      audioUrl = `${API_URL}/uploads/${filename}`;
    } else {
      alert('No audio file available for this item');
      return;
    }

    // Create new audio element
    const audio = new Audio(audioUrl);
    audio.addEventListener('ended', () => {
      setPlayingIndex(null);
      audioRef.current = null;
    });
    audio.addEventListener('error', (e) => {
      console.error('Audio playback error:', e);
      let errorMessage = 'Failed to play audio file.';
      if (audio.error) {
        switch (audio.error.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorMessage = 'Audio playback was aborted.';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorMessage = 'Network error while loading audio.';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorMessage = 'Audio decoding error.';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = 'Audio format not supported.';
            break;
        }
      }
      alert(errorMessage);
      setPlayingIndex(null);
      audioRef.current = null;
    });

    audio.play().catch((error) => {
      console.error('Error playing audio:', error);
      let errorMessage = 'Failed to play audio file: ' + error.message;
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Autoplay was blocked. Please click the play button again.';
      }
      alert(errorMessage);
      setPlayingIndex(null);
      audioRef.current = null;
    });
    
    setPlayingIndex(index);
    audioRef.current = audio;
  };

  const handleStopItem = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingIndex(null);
    setPlayingVoicetrackId(null);
    audioRef.current = null;
  };

  const handlePlayVoicetrackById = (vtId) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    const audioUrl = `${API_URL}/api/voicetrack-enhanced/${vtId}/audio`;
    const audio = new Audio(audioUrl);
    audio.addEventListener('ended', () => {
      setPlayingVoicetrackId(null);
      audioRef.current = null;
    });
    audio.addEventListener('error', () => {
      setPlayingVoicetrackId(null);
      audioRef.current = null;
    });
    audio.play().catch(() => {
      setPlayingVoicetrackId(null);
      audioRef.current = null;
    });
    setPlayingVoicetrackId(vtId);
    setPlayingIndex(null);
    audioRef.current = audio;
  };

  const handlePauseItem = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setPlayingIndex(null);
    setPlayingVoicetrackId(null);
    audioRef.current = null;
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, []);

  const timedPlaylistItems = calculateItemTimings();
  const groupedPlaylistItems = groupCommercialBreaks(timedPlaylistItems);

  // Summary stats for the header
  const totalDurationSecs = playlistItems.reduce((sum, i) => sum + (i.duration || 0), 0);
  const vtMarkerCount = playlistItems.filter(i => i.type === 'voicetrack' || i.type === 'voice').length;
  const vtRecordedCount = playlistItems.filter((item, idx) => {
    if (item.type !== 'voicetrack' && item.type !== 'voice') return false;
    if (item.voicetrack_id) return true;
    const saved = (item.id ? voicetracksByPlayoutLog[item.id] : voicetracksByIndex[idx]) || [];
    return saved.length > 0;
  }).length;
  const selectedStationName = stations.find(s => String(s.id) === String(selectedStation))?.name;
  const selectedRegionName = selectedRegion ? regions.find(r => r.id === selectedRegion)?.name : null;

  const sectionLabelSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 0.5,
    color: '#7dd3fc',
    fontWeight: 700,
    letterSpacing: '0.12em',
    fontSize: '0.7rem',
    textTransform: 'uppercase',
  };

  const headerCellSx = {
    color: 'var(--rs-muted)',
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    borderBottom: '1px solid rgba(148,163,184,0.14)',
    bgcolor: 'var(--rs-overlay-strong)',
    backdropFilter: 'blur(6px)',
    whiteSpace: 'nowrap',
  };

  return (
    <Box>
      <PageHeader
        overline="Presenting"
        title="Voicetracking"
        subtitle="Record and place voice links across your playout hour"
        icon={<Mic />}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: 3,
        }}
      >
        {/* Left Panel: collapses horizontally to a rail */}
        <Box
          sx={{
            flexShrink: 0,
            width: leftPanelOpen ? { xs: '100%', md: 360 } : 56,
            transition: 'width 280ms ease',
            overflow: 'hidden',
          }}
        >
          {!leftPanelOpen ? (
            <Card
              sx={{
                height: '100%',
                minHeight: 280,
                animation: 'card-rise 420ms ease-out both',
              }}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  px: 1,
                  py: 1.5,
                  height: '100%',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setLeftPanelOpen(true)}
                  sx={{ color: '#7dd3fc' }}
                  aria-label="Expand menus"
                >
                  <ChevronRight />
                </IconButton>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2.5,
                    mt: 1,
                    flex: 1,
                  }}
                >
                  <Box
                    onClick={() => setLeftPanelOpen(true)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      color: '#7dd3fc',
                      '&:hover': { color: '#a5e8ff' },
                    }}
                    title="Playlist"
                  >
                    <QueueMusic sx={{ fontSize: 20 }} />
                    <Typography
                      sx={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Playlist
                    </Typography>
                  </Box>
                  <Box
                    onClick={() => setLeftPanelOpen(true)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      color: '#7dd3fc',
                      '&:hover': { color: '#a5e8ff' },
                    }}
                    title="Input"
                  >
                    <GraphicEq sx={{ fontSize: 20 }} />
                    <Typography
                      sx={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Input
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: { xs: '100%', md: 360 } }}>
              <Card sx={{ animation: 'card-rise 420ms ease-out both' }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ ...sectionLabelSx, mb: 0 }}>
                      <QueueMusic sx={{ fontSize: 16 }} /> Playlist
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ color: '#7dd3fc' }}
                      onClick={() => setLeftPanelOpen(false)}
                      aria-label="Collapse menus to the left"
                    >
                      <ChevronLeft />
                    </IconButton>
                  </Box>
                  {/* Console-style target readout */}
                  <Box
                    sx={{
                      mt: 1.5,
                      mb: 2,
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 999,
                      bgcolor: 'var(--rs-surface)',
                      border: '1px solid var(--rs-live-console-border-active)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'var(--rs-accent)',
                        boxShadow: '0 0 8px var(--rs-live-console-glow)',
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '0.58rem',
                          fontWeight: 700,
                          letterSpacing: '0.16em',
                          textTransform: 'uppercase',
                          color: 'var(--rs-muted)',
                        }}
                      >
                        Target Hour
                      </Typography>
                      <Typography
                        noWrap
                        className="tabular-nums"
                        sx={{
                          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: 'var(--rs-text)',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {selectedDate} · {String(selectedHour).padStart(2, '0')}:00{regions.length > 1 ? ` · ${(selectedRegionName || 'MASTER').toUpperCase()}` : ''}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Station Selection */}
                  {stations.length > 1 && (
                    <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                      <InputLabel>Station</InputLabel>
                      <Select
                        value={selectedStation || ''}
                        onChange={(e) => setSelectedStation(e.target.value)}
                        label="Station"
                      >
                        {stations.map((station) => (
                          <MenuItem key={station.id} value={station.id}>
                            {station.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Date Selection */}
                  <TextField
                    fullWidth
                    type="date"
                    label="Date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />

                  {/* Hour Selection */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Hour</InputLabel>
                    <Select
                      value={selectedHour}
                      onChange={(e) => setSelectedHour(e.target.value)}
                      label="Hour"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <MenuItem key={i} value={i}>
                          {i.toString().padStart(2, '0')}:00
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Region Selection for Split Voicetracks */}
                  {regions.length > 0 && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Region (Split VT)</InputLabel>
                      <Select
                        value={selectedRegion || ''}
                        onChange={(e) => setSelectedRegion(e.target.value || null)}
                        label="Region (Split VT)"
                      >
                        <MenuItem value="">
                          <em>Master / Shared (All Regions)</em>
                        </MenuItem>
                        {regions.filter(r => r.is_active).map((region) => (
                          <MenuItem key={region.id} value={region.id}>
                            {region.name} ({region.code})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={fetchPlaylist}
                    disabled={loadingPlaylist || !selectedStation}
                    startIcon={loadingPlaylist ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <QueueMusic />}
                    sx={{
                      py: 1.1,
                      backgroundImage: 'linear-gradient(180deg, #34dced 0%, #0891b2 100%)',
                      color: '#06181d',
                      fontWeight: 700,
                      '&:hover': {
                        backgroundImage: 'linear-gradient(180deg, #4ce4f4 0%, #0ea5c9 100%)',
                        boxShadow: '0 8px 24px -8px rgba(34,211,238,0.55)',
                      },
                    }}
                  >
                    {loadingPlaylist ? 'Loading…' : 'Load Playlist'}
                  </Button>

                  {regions.length > 0 && selectedRegion && (
                    <Alert
                      severity="info"
                      icon={<Public fontSize="small" />}
                      sx={{
                        mt: 1.5,
                        borderRadius: 2,
                        border: '1px solid rgba(34,211,238,0.25)',
                        bgcolor: 'rgba(34,211,238,0.08)',
                      }}
                    >
                      <Typography variant="caption">
                        Recording voicetracks for <strong>{regions.find(r => r.id === selectedRegion)?.name}</strong> region.
                        These will only play on the regional stream.
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Audio Input Device Selection */}
              <Card sx={{ animation: 'card-rise 420ms ease-out 90ms both' }}>
                <CardContent>
                  <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
                    <GraphicEq sx={{ fontSize: 16 }} /> Input
                  </Typography>
                  <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                    Audio Input Device
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Select your local microphone/audio input device
                  </Typography>

                  <FormControl fullWidth>
                    <InputLabel>Microphone</InputLabel>
                    <Select
                      value={selectedDevice}
                      onChange={(e) => setSelectedDevice(e.target.value)}
                      label="Microphone"
                    >
                      {devices.length === 0 ? (
                        <MenuItem disabled>No devices found. Please allow microphone access.</MenuItem>
                      ) : (
                        devices.map((device) => (
                          <MenuItem key={device.deviceId} value={device.deviceId}>
                            {device.label || `Device ${device.deviceId.slice(0, 8)}`}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>

                  {devices.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#22c55e',
                          boxShadow: '0 0 8px rgba(34,197,94,0.8)',
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {devices.find(d => d.deviceId === selectedDevice)?.label || 'Device selected'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>

        {/* Right Panel: Playlist Table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ animation: 'card-rise 420ms ease-out 140ms both' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography sx={sectionLabelSx}>
                    <Schedule sx={{ fontSize: 16 }} />
                    {selectedDate} · {String(selectedHour).padStart(2, '0')}:00
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'text.primary' }}>
                    Playout Log
                  </Typography>
                  {playlistItems.length > 0 && !loadingPlaylist && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
                      {stations.length > 1 && (
                        <StatPill label="Station" value={selectedStationName || '—'} />
                      )}
                      {regions.length > 1 && (
                        <StatPill label="Region" value={selectedRegionName || 'Master'} />
                      )}
                      <StatPill label="Items" value={playlistItems.length} accent="var(--rs-text)" />
                      <StatPill label="Runtime" value={formatDuration(totalDurationSecs)} accent="var(--rs-text)" />
                      <StatPill
                        label="Voicetracks"
                        value={`${vtRecordedCount}/${vtMarkerCount}`}
                        accent={vtMarkerCount > 0 && vtRecordedCount === vtMarkerCount ? '#4ade80' : '#c084fc'}
                      />
                    </Box>
                  )}
                  {selectedItems.length > 0 && (
                    <Chip
                      icon={<Mic sx={{ fontSize: 14 }} />}
                      size="small"
                      label={selectedItems.length === 1
                        ? '1 item selected — pick another to place a VT between them'
                        : '2 items selected — VT marker will be inserted between them'}
                      sx={{
                        mt: 0.75,
                        bgcolor: 'rgba(34,211,238,0.12)',
                        border: '1px solid rgba(34,211,238,0.35)',
                        color: '#7dd3fc',
                        '& .MuiChip-icon': { color: '#7dd3fc' },
                      }}
                    />
                  )}
                </Box>
                <Button
                  variant="contained"
                  onClick={addVTMarker}
                  disabled={!selectedStation || loadingPlaylist}
                  sx={{
                    backgroundImage: 'linear-gradient(180deg, #a855f7 0%, #9333ea 100%)',
                    '&:hover': {
                      backgroundImage: 'linear-gradient(180deg, #c084fc 0%, #a855f7 100%)',
                      boxShadow: '0 8px 24px -8px rgba(168,85,247,0.6)',
                    },
                  }}
                  startIcon={<Mic />}
                >
                  Add VT Marker
                  {selectedItems.length === 2 && ' (Between Selected)'}
                </Button>
              </Box>

              {loadingPlaylist ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
                  <CircularProgress size={32} sx={{ color: '#22d3ee' }} />
                  <Typography color="text.secondary">Loading playlist…</Typography>
                </Box>
              ) : playlistItems.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 8 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(168,85,247,0.12)',
                      border: '1px solid rgba(192,132,252,0.3)',
                    }}
                  >
                    <Campaign sx={{ fontSize: 30, color: '#c084fc' }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    No playlist loaded
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Pick a station, date and hour on the left, then load the playlist to start voicetracking.
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)', borderRadius: 2 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>#</TableCell>
                        <TableCell sx={headerCellSx}>Time</TableCell>
                        <TableCell sx={{ ...headerCellSx, width: 40, p: 0.5 }}></TableCell>
                        <TableCell sx={headerCellSx}>Type</TableCell>
                        <TableCell sx={headerCellSx}>Title</TableCell>
                        <TableCell sx={headerCellSx}>Artist</TableCell>
                        <TableCell sx={headerCellSx}>Duration</TableCell>
                        <TableCell sx={headerCellSx}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedPlaylistItems.map((entry, gIdx) => {
                        // ---- Collapsible Commercial Break Group ----
                        if (entry.type === 'commercial_break_group') {
                          const isExpanded = expandedBreaks.has(entry.groupStartIndex);
                          const rows = [];
                          // Summary row (always visible)
                          rows.push(
                            <TableRow
                              key={`break-group-${entry.groupStartIndex}`}
                              onClick={() => toggleBreakExpanded(entry.groupStartIndex)}
                              sx={{
                                bgcolor: hexToRgba('#f59e0b', 0.09),
                                '&:hover': { bgcolor: hexToRgba('#f59e0b', 0.18) },
                                cursor: 'pointer',
                                '& td': { borderBottom: '1px solid rgba(148,163,184,0.08)' },
                                '& td:first-of-type': { borderLeft: '3px solid #f59e0b' },
                                transition: 'background-color 140ms ease',
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {isExpanded ? <ExpandLess sx={{ color: '#f59e0b' }} /> : <ExpandMore sx={{ color: '#f59e0b' }} />}
                                  {entry.displayNum}
                                </Box>
                              </TableCell>
                              <TableCell className="tabular-nums" sx={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', color: 'var(--rs-muted)' }}>
                                {entry.formattedStartTime}
                              </TableCell>
                              <TableCell sx={{ width: 40, p: 0.5 }} />
                              <TableCell>
                                <Chip
                                  icon={<Campaign sx={{ fontSize: 14 }} />}
                                  label="COMMERCIAL"
                                  size="small"
                                  sx={{
                                    bgcolor: hexToRgba('#f59e0b', 0.18),
                                    color: '#ffb74d',
                                    border: '1px solid rgba(245,158,11,0.4)',
                                    fontWeight: 700,
                                    fontSize: '0.66rem',
                                    letterSpacing: '0.04em',
                                    '& .MuiChip-icon': { color: '#ffb74d' },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Commercial Break
                                <Typography component="span" variant="caption" sx={{ color: 'var(--rs-muted)', ml: 1 }}>
                                  {entry.spotCount} spot{entry.spotCount !== 1 ? 's' : ''}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>
                              </TableCell>
                              <TableCell className="tabular-nums">
                                {formatDuration(entry.totalDuration || 0)}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          );
                          // Expanded individual ad rows
                          if (isExpanded) {
                            let adTime = entry.startTime ? new Date(entry.startTime) : null;
                            entry.items.forEach((adItem, adIdx) => {
                              const adOrigIdx = adItem.originalIndex;
                              const isPlaying = playingIndex === adOrigIdx;
                              const hasAudio = adItem.filepath;
                              const adStartFormatted = adTime ? formatTime(adTime.toISOString()) : (adItem.formattedStartTime || '');
                              rows.push(
                                <TableRow
                                  key={`break-ad-${adOrigIdx}`}
                                  sx={{
                                    bgcolor: hexToRgba('#f59e0b', 0.04),
                                    '&:hover': { bgcolor: hexToRgba('#f59e0b', 0.12) },
                                    '& td': { borderBottom: '1px solid rgba(148,163,184,0.06)' },
                                    '& td:first-of-type': { borderLeft: '3px solid rgba(245,158,11,0.35)' },
                                  }}
                                >
                                  <TableCell sx={{ pl: 5, color: 'var(--rs-muted)' }}>
                                    {adIdx + 1}
                                  </TableCell>
                                  <TableCell className="tabular-nums" sx={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                                    {adStartFormatted}
                                  </TableCell>
                                  <TableCell sx={{ width: 40, p: 0.5 }} />
                                  <TableCell>
                                    <Chip
                                      label="AD"
                                      size="small"
                                      sx={{
                                        bgcolor: hexToRgba('#e68900', 0.16),
                                        color: '#ffb74d',
                                        border: '1px solid rgba(230,137,0,0.35)',
                                        fontWeight: 700,
                                        fontSize: '0.66rem',
                                        height: 20,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'medium', fontSize: '0.85rem' }}>
                                    {adItem.title || 'Spot'}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                    {adItem.artist || ''}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.85rem' }}>
                                    {formatDuration(adItem.duration || 0)}
                                  </TableCell>
                                  <TableCell>
                                    {hasAudio && (
                                      <>
                                        {isPlaying ? (
                                          <>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handlePauseItem(); }} sx={{ color: '#22d3ee' }}>
                                              <Pause fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleStopItem(); }} sx={{ color: '#ff6b6b' }}>
                                              <Stop fontSize="small" />
                                            </IconButton>
                                          </>
                                        ) : (
                                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handlePlayItem(adOrigIdx); }} sx={{ color: '#22c55e' }}>
                                            <PlayArrow fontSize="small" />
                                          </IconButton>
                                        )}
                                      </>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                              if (adTime) {
                                adTime = new Date(adTime.getTime() + (adItem.duration || 0) * 1000);
                              }
                            });
                          }
                          return rows;
                        }

                        // ---- Normal item (non-commercial) ----
                        const item = entry;
                        const index = entry.originalIndex;
                        const isVoicetrackMarker = item.type === 'voicetrack' || item.type === 'voice';
                        const { categoryName, categoryColor } = getCategoryInfo(item);
                        const savedVts = (isVoicetrackMarker && (item.id ? voicetracksByPlayoutLog[item.id] : voicetracksByIndex[index])) || [];
                        const hasAudio = item.asset_id || item.filepath || (isVoicetrackMarker && (item.voicetrack_id || savedVts.length > 0));
                        const isPlaying = playingIndex === index;
                        
                        return (
                          <TableRow
                            key={index}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={() => handleDragEnd()}
                            onClick={(e) => {
                              if (isVoicetrackMarker) {
                                handleMarkerClick(item, index);
                              } else {
                                if (e.target.closest('button') || e.target.closest('[role="button"]')) {
                                  return;
                                }
                                handleItemSelect(index);
                              }
                            }}
                            sx={{
                              bgcolor: selectedItems.includes(index)
                                ? hexToRgba('#22d3ee', 0.16)
                                : isVoicetrackMarker
                                  ? hexToRgba('#a855f7', 0.12)
                                  : hexToRgba(categoryColor, 0.06),
                              '&:hover': {
                                bgcolor: selectedItems.includes(index)
                                  ? hexToRgba('#22d3ee', 0.24)
                                  : isVoicetrackMarker
                                    ? hexToRgba('#a855f7', 0.2)
                                    : hexToRgba(categoryColor, 0.14),
                              },
                              '& td': { borderBottom: '1px solid rgba(148,163,184,0.08)' },
                              '& td:first-of-type': {
                                borderLeft: selectedItems.includes(index)
                                  ? '3px solid #22d3ee'
                                  : `3px solid ${categoryColor}`,
                              },
                              cursor: draggedIndex === index ? 'grabbing' : 'grab',
                              opacity: draggedIndex === index ? 0.5 : 1,
                              transition: 'background-color 140ms ease, opacity 140ms ease',
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DragIndicator
                                  sx={{
                                    fontSize: 18,
                                    color: 'rgba(148,163,184,0.4)',
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing' },
                                  }}
                                />
                                {entry.displayNum}
                              </Box>
                            </TableCell>
                            <TableCell className="tabular-nums" sx={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', color: 'var(--rs-muted)' }}>
                              {entry.formattedStartTime}
                            </TableCell>
                            <TableCell sx={{ width: 40, p: 0.5 }}>
                              {!isVoicetrackMarker
                                && hasSystemFeature('ai_insights')
                                && item.category?.ai_insights_enabled && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAiInsightsItem(item);
                                    setAiInsightsOpen(true);
                                  }}
                                  title="AI Insights"
                                  sx={{ color: '#22d3ee' }}
                                >
                                  <Psychology fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={isVoicetrackMarker ? <Mic sx={{ fontSize: 13 }} /> : undefined}
                                label={isVoicetrackMarker ? 'VOICE' : categoryName}
                                size="small"
                                sx={{
                                  bgcolor: hexToRgba(isVoicetrackMarker ? '#c084fc' : categoryColor, 0.16),
                                  color: isVoicetrackMarker ? '#7c3aed' : 'var(--rs-text)',
                                  border: `1px solid ${hexToRgba(isVoicetrackMarker ? '#c084fc' : categoryColor, 0.45)}`,
                                  fontWeight: 700,
                                  fontSize: '0.66rem',
                                  letterSpacing: '0.04em',
                                  '& .MuiChip-icon': { color: '#c084fc' },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {item.title || item.asset?.title || 'Unknown Item'}
                                {item.type === 'mixblock_track' && item.mixblock_position != null && (
                                  <Chip
                                    label={`Mix ${item.mixblock_position + 1}/${item.mixblock_total || '?'}`}
                                    size="small"
                                    sx={{
                                      bgcolor: hexToRgba('#e040fb', 0.16),
                                      color: '#f0abfc',
                                      border: '1px solid rgba(224,64,251,0.4)',
                                      fontSize: '0.65rem',
                                      height: 18,
                                      ml: 0.5,
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {item.artist || item.asset?.artist || ''}
                            </TableCell>
                            <TableCell>
                              {item.type !== 'mixblock_track' && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box component="span" className="tabular-nums">
                                  {formatDuration(
                                    isVoicetrackMarker && savedVts.length > 0
                                      ? Math.max(...savedVts.map(vt => vt.duration || 0))
                                      : (item.duration || 0)
                                  )}
                                </Box>
                                {item.intro && item.intro > 0 && (
                                  <Chip
                                    label={`Intro ${item.intro.toFixed(1)}s`}
                                    size="small"
                                    sx={{
                                      bgcolor: hexToRgba('#22c55e', 0.16),
                                      color: '#4ade80',
                                      border: '1px solid rgba(76,175,80,0.4)',
                                      fontSize: '0.66rem',
                                      height: 20,
                                    }}
                                  />
                                )}
                              </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {/* Saved voicetracks (including split per region) - each with play button */}
                                {isVoicetrackMarker && savedVts.length > 0 && savedVts.map((vt) => {
                                  const vtRegionName = vt.region_id && regions.length
                                    ? (regions.find(r => r.id === vt.region_id)?.name || `R${vt.region_id}`)
                                    : 'Shared';
                                  const isVtPlaying = playingVoicetrackId === vt.id;
                                  return (
                                    <Box
                                      key={vt.id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.25,
                                        pl: 1,
                                        pr: 0.25,
                                        py: 0.25,
                                        borderRadius: 999,
                                        bgcolor: isVtPlaying ? 'rgba(34,211,238,0.12)' : 'rgba(148,163,184,0.08)',
                                        border: `1px solid ${isVtPlaying ? 'rgba(34,211,238,0.4)' : 'rgba(148,163,184,0.18)'}`,
                                        transition: 'background-color 140ms ease, border-color 140ms ease',
                                      }}
                                    >
                                      {isVtPlaying && <EqualizerBars color="#22d3ee" height={10} />}
                                      <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 600, color: isVtPlaying ? '#7dd3fc' : '#c3ccd9' }}>
                                        {vtRegionName}
                                      </Typography>
                                      {isVtPlaying ? (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => { e.stopPropagation(); handlePauseItem(); }}
                                          sx={{ color: '#22d3ee', p: 0.25 }}
                                        >
                                          <Pause fontSize="small" />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => { e.stopPropagation(); handlePlayVoicetrackById(vt.id); }}
                                          sx={{ color: '#22c55e', p: 0.25 }}
                                        >
                                          <PlayArrow fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  );
                                })}
                                {/* Fallback: single play button when no savedVts but item has audio */}
                                {hasAudio && savedVts.length === 0 && (
                                  <>
                                    {isPlaying ? (
                                      <>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => { e.stopPropagation(); handlePauseItem(); }}
                                          sx={{ color: '#22d3ee' }}
                                        >
                                          <Pause fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => { e.stopPropagation(); handleStopItem(); }}
                                          sx={{ color: '#ff6b6b' }}
                                        >
                                          <Stop fontSize="small" />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handlePlayItem(index); }}
                                        sx={{ color: '#22c55e' }}
                                      >
                                        <PlayArrow fontSize="small" />
                                      </IconButton>
                                    )}
                                  </>
                                )}
                                
                                {isVoicetrackMarker && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMarkerClick(item, index);
                                    }}
                                    sx={{
                                      backgroundImage: 'linear-gradient(180deg, #a855f7 0%, #9333ea 100%)',
                                      '&:hover': {
                                        backgroundImage: 'linear-gradient(180deg, #c084fc 0%, #a855f7 100%)',
                                        boxShadow: '0 6px 18px -6px rgba(168,85,247,0.6)',
                                      },
                                    }}
                                    startIcon={<Mic />}
                                  >
                                    Record
                                  </Button>
                                )}

                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveItem(index);
                                  }}
                                  sx={{
                                    color: 'rgba(255,107,107,0.55)',
                                    '&:hover': { color: '#ff6b6b', bgcolor: 'rgba(255,107,107,0.1)' },
                                  }}
                                  title="Remove from playlist"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Voicetrack Marker Recorder Modal */}
      {selectedMarkerItem && selectedMarkerIndex >= 0 && (
        <VoicetrackMarkerRecorder
          open={showVoicetrackRecorder}
          onClose={() => {
            setShowVoicetrackRecorder(false);
            setSelectedMarkerItem(null);
            setSelectedMarkerIndex(-1);
            setSavedVoicetracksForMarker([]);
          }}
          markerItem={selectedMarkerItem}
          allItems={playlistItems}
          markerIndex={selectedMarkerIndex}
          onSave={handleVoicetrackSave}
          assets={assets}
          regions={regions.filter(r => r.is_active)}
          selectedRegion={selectedRegion}
          savedVoicetracks={savedVoicetracksForMarker}
          apiUrl={API_URL}
          stationId={selectedStation}
          onRefreshSaved={() => fetchVoicetracksForMarker(selectedMarkerItem?.id || null)}
          musicBedCategories={musicBedCategories}
          musicBedAssets={musicBedAssets}
        />
      )}

      <AiInsightsDialog
        open={aiInsightsOpen}
        onClose={() => { setAiInsightsOpen(false); setAiInsightsItem(null); }}
        item={aiInsightsItem}
        stationId={selectedStation || null}
      />
    </Box>
  );
}
